import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@predoc/cow';

export type PaginationState = {
  page: number; // start from 0
  pageSize: number;
};

type PaginationProps = {
  pagination: PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  totalCount: number;
  pageSizeOptions?: number[];
};

export const Pagination = ({
  pagination,
  setPagination,
  totalCount,
  pageSizeOptions = [5, 10, 20],
}: PaginationProps) => {
  // make sure the pageSize is valid, otherwise use the first option
  const validPageSize = pageSizeOptions.includes(pagination.pageSize)
    ? pagination.pageSize
    : pageSizeOptions[0];

  // calculate related values
  const pageCount = Math.ceil(totalCount / validPageSize);
  const firstRowIndex = pagination.page * validPageSize + 1;
  const lastRowIndex = Math.min(firstRowIndex + validPageSize - 1, totalCount);

  const handleSizeChange = (value: string) => {
    setPagination({
      page: 0,
      pageSize: Number(value),
    });
  };

  const handlePageChange = (value: number) => {
    setPagination({
      page: value,
      pageSize: validPageSize,
    });
  };

  const handlePreviousPage = () => {
    handlePageChange(pagination.page - 1);
  };

  const handleNextPage = () => {
    handlePageChange(pagination.page + 1);
  };

  return (
    <div className="mt-4 flex items-center justify-start gap-2">
      <span className="text-sm font-medium">Rows per page:</span>
      <Select value={`${validPageSize}`} onValueChange={handleSizeChange}>
        <SelectTrigger className="h-8 w-[70px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {pageSizeOptions.map((size) => (
            <SelectItem key={size} value={`${size}`}>
              {size}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground text-sm">
        {firstRowIndex}–{lastRowIndex} of {totalCount}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={handlePreviousPage}
        disabled={pagination.page === 0}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={handleNextPage}
        disabled={pagination.page >= pageCount - 1}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};
